import React, { type FunctionComponent, useEffect, useState } from 'react'
import { AccordionPanel } from '../../../../common/accordion'
import { CampaignReview } from './CampaignReview'
import { API_HOST, authenticatedFetchData } from '../../../../../lib'
import { logError } from '@tomra/datadog-browser-logging'
import { Status } from '../../../../common/styled/Status'
import { useSearchParams } from 'react-router-dom'
import { fetchPendingCampaign } from '../../../../../services/charity.service'

type Props = {
  charityId: string
}

export const CharityCampaigns: FunctionComponent<Props> = ({ charityId }) => {
  const [status, setStatus] = useState<RequestStatusType>('idle')
  const [campaigns, setCampaigns] = useState<CampaignSummaryType[]>([])
  const [campaignId, setCampaignId] = useState<string | null>(null)
  const [campaignIdsWithPendingChanges, setCampaignIdsWithPendingChanges] = useState<string[]>([])

  const [pendingChangesStatus, setPendingChangesStatus] = useState<RequestStatusType>('pending')

  const [searchParams] = useSearchParams()

  useEffect(() => {
    const campaignId = searchParams.get('campaignId')
    setCampaignId(campaignId)
  }, [])

  const campaignChanged = (campaign: CharityCampaignType) => {
    setCampaigns(
      campaigns.map(c =>
        campaign.campaignId === c.id
          ? {
              ...c,
              name: campaign.name,
              startTime: campaign.startTime,
              endTime: campaign.endTime,
              status: campaign.status
            }
          : c
      )
    )

    setCampaignIdsWithPendingChanges(prev => prev.filter(campaignId => campaignId !== campaign.campaignId))
  }

  function sortByStatus(campaignA: CampaignSummaryType, campaignB: CampaignSummaryType) {
    const statusOrder = ['NEW', 'ACTIVE', 'STOPPED', 'EXPIRED']
    return statusOrder.indexOf(campaignA.status) - statusOrder.indexOf(campaignB.status)
  }

  async function fetchPendingChangesForActiveCampaigns(campaigns: CampaignSummaryType[]) {
    try {
      const campaignsWithChanges = await Promise.all(
        campaigns.map(campaign => fetchPendingCampaign(campaign.charitySummary.id, campaign.id))
      )

      setCampaignIdsWithPendingChanges(
        campaignsWithChanges.filter(campaign => campaign !== null).map(campaign => campaign.campaignId)
      )

      setPendingChangesStatus('idle')
    } catch (error) {
      logError(new Error('Failed to fetch pending changes for active campaigns'), error as Error)
      setStatus('failure')
    }
  }

  useEffect(() => {
    setStatus('pending')

    const { run, abort } = authenticatedFetchData(`${API_HOST}/charities/admin/v1/charities/${charityId}/campaign`)

    run()
      .then(response => {
        setStatus('idle')
        setCampaigns(response)

        return fetchPendingChangesForActiveCampaigns(
          response.filter(
            (campaign: CampaignSummaryType) => campaign.status !== 'STOPPED' && campaign.status !== 'EXPIRED'
          )
        )
      })
      .catch((error: any) => {
        if (error.name !== 'AbortError') {
          logError(new Error('Failed to load charity campaigns'), error)
          setStatus('failure')
        }
      })

    return () => abort()
  }, [charityId])

  if (status === 'failure') {
    return (
      <div>
        <h1 className="sr-only">Campaigns</h1>
        <div className="alert alert-danger">Failed to load charity campaigns</div>
      </div>
    )
  }

  return (
    <>
      <h2 className="text-md mb-sm ml-md">Campaigns</h2>

      {status === 'pending' ? (
        <div className="loadingSpinner ml-md mt-md" />
      ) : campaigns.length === 0 ? (
        <div className="mt-md ml-md">No campaigns have been submitted by this charity yet.</div>
      ) : campaigns.length > 0 ? (
        <>
          {campaigns.sort(sortByStatus).map((campaign, i) => {
            const hasPendingChanges = campaignIdsWithPendingChanges.includes(campaign.id)
            const isNotExpiredOrStopped = campaign.status !== 'EXPIRED' && campaign.status !== 'STOPPED'

            return (
              <AccordionPanel
                key={`ap-${i + 1}`}
                isOpen={campaign.id === campaignId}
                ariaLabel={`Show campaign: ${campaign.name}`}
                title={
                  <div className="flex-1">
                    <Status status={campaign.status} campaignStartDate={campaign.startTime} />

                    <div className="flex justify-between items-center">
                      <span>{campaign.name}</span>

                      {isNotExpiredOrStopped && pendingChangesStatus === 'pending' && (
                        <div className="loadingSpinner text-sm" />
                      )}

                      {hasPendingChanges && (
                        <div>
                          <span className="tag tag-orange">Changed campaign</span>
                        </div>
                      )}
                    </div>
                  </div>
                }
                trailingElement={
                  <div className="text-right">
                    <div className="font-bold mt-xs">
                      {new Date(campaign.startTime).toLocaleDateString()} -{' '}
                      {new Date(campaign.endTime).toLocaleDateString()}
                    </div>
                  </div>
                }
              >
                <CampaignReview charityId={charityId} campaignId={campaign.id} campaignChanged={campaignChanged} />
              </AccordionPanel>
            )
          })}
        </>
      ) : null}
    </>
  )
}
